import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import PublicHeader from '../components/PublicHeader';
import {
  Engineering,
  Payments,
  WorkspacePremium,
  TrendingUp,
  HomeOutlined,
  FactCheck,
  VerifiedUser,
  Handshake,
  Check,
  ArrowForward,
  ExpandMore,
  Badge as BadgeIcon,
  PhotoCamera,
  Article,
  Groups,
  ReceiptLong,
} from '@mui/icons-material';

/* ───────────────────────────────────────────────────────────────────────
   FOR CONTRACTORS — public marketing + application landing.
   Shares the HomePage design language (Figtree, Deep-Teal palette,
   eyebrow labels, tinted cards, 4-step grid). The deep-teal bands are the
   ownable "builder zone" that distinguishes this audience from the
   client-facing white sections on the home page.
   ─────────────────────────────────────────────────────────────────────── */

const C = {
  green: '#185451',
  greenDeep: '#134e4a',
  greenPillBg: '#D7F0EC',
  ink: '#41464A',
  ink2: '#33373A',
  gray: '#5E6366',
  grayFaint: '#9FA8A3',
  line: '#E7EBEA',
  card1: '#EAF3EC',
  card2: '#F1F4F3',
  card3: '#ECEFEE',
  tintBg: '#F5F7F6',
  ctaBg: '#3C4143',
  ctaAccent: '#6EE7D8',
};
const FONT = "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
const APPLY = '/register?role=contractor';

const SectionLabel = ({ children, light = false, align = 'center' }) => (
  <Box sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: light ? C.ctaAccent : C.green, mb: '0.6rem', textAlign: align }}>
    {children}
  </Box>
);

const SectionH2 = ({ children, light = false, align = 'center' }) => (
  <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.8rem,3.5vw,2.6rem)', fontWeight: 800, letterSpacing: '-0.03em', color: light ? '#fff' : C.ink2, lineHeight: 1.15, textAlign: align, mb: '0.85rem', mt: 0 }}>
    {children}
  </Box>
);

const ForContractorsPage = () => {
  const navigate = useNavigate();
  const go = (path) => () => navigate(path);
  const [openFaq, setOpenFaq] = useState(0);

  const benefits = [
    { bg: C.card1, accent: C.green, icon: <Engineering />, title: 'Diaspora-funded projects', body: 'Get matched with serious clients funding real builds from abroad — full homes, renovations and repairs across Zimbabwe.' },
    { bg: C.card2, accent: C.green, icon: <Payments />, title: 'Paid for every milestone', body: 'Funds are secured up front and released to you as each stage is completed and signed off. No chasing payment.' },
    { bg: C.card3, accent: C.green, icon: <WorkspacePremium />, title: 'A verified badge clients trust', body: 'Your verified profile, portfolio and references give diaspora clients the confidence to hire you over an unknown name.' },
    { bg: C.card1, accent: C.green, icon: <TrendingUp />, title: 'Build your reputation', body: 'Ratings, a job count and reviews that grow with every job paid through Eqaya — and bring you more work. Cash jobs don\'t count.' },
  ];

  const steps = [
    { badge: 'Step 1', icon: <HomeOutlined />, title: 'Apply', body: 'Create your account and complete the application — business details, photos, portfolio and references.' },
    { badge: 'Step 2', icon: <FactCheck />, title: 'We verify', body: 'Our team reviews your documents, checks your portfolio and contacts your references.' },
    { badge: 'Step 3', icon: <VerifiedUser />, title: 'Get approved', body: 'Once approved you earn your verified badge and full access to the marketplace.' },
    { badge: 'Step 4', icon: <Handshake />, title: 'Win work', body: 'Browse diaspora-funded projects, submit your quotes, and get hired with confidence.' },
  ];

  const requirements = [
    { icon: <Article />, label: 'Business name & contact', note: 'Phone, email and a business address.' },
    { icon: <PhotoCamera />, label: 'Business logo & a public business photo', note: 'Your logo (or photo) for your card, plus a photo of your work — shown publicly.' },
    { icon: <BadgeIcon />, label: 'Government-issued Photo ID', note: 'Kept private — used only to verify your identity.' },
    { icon: <ReceiptLong />, label: 'Certificate of incorporation', note: 'Proof your business is registered. Tax clearance is optional.' },
    { icon: <PhotoCamera />, label: 'A portfolio of past projects', note: 'At least one project, each with 6 or more photos.' },
    { icon: <Groups />, label: 'Two or more references', note: 'Client or trade references we can contact.' },
  ];

  const faqs = [
    { q: 'How much does it cost to apply?', a: 'Applying is free. You only pay a small platform fee on work you actually complete and get paid for.' },
    { q: 'How long does verification take?', a: 'Usually a few business days once your application is complete and your references respond. We will email you as soon as a decision is made.' },
    { q: "I'm a sole trader, not a company. Can I still apply?", a: 'Yes. Choose the “Individual” option when you apply — your business name can simply be your own trading name.' },
    { q: 'What if my application is rejected?', a: 'You will see exactly what needs changing, and you can edit your application and resubmit it for another review. Rejection is rarely the end of the road.' },
    { q: 'When and how do I get paid?', a: 'Client funds are secured before work begins and released to you as each milestone is completed and verified. You withdraw your balance whenever you like.' },
    { q: "I don't have every document yet. Can I still start?", a: 'You can begin your application and save your progress. Some requirements can be waived case by case by our team during review.' },
    { q: 'Can I meet an Eqaya client and do the job for cash instead?', a: 'You can talk to clients by phone or in person whenever you like. But a job that is paid outside Eqaya does not count: no review, no addition to your job count, no Guarantee for the client, and no dispute support for you. Send the quotation on Eqaya when you agree a price and the client funds each stage before you start.' },
  ];

  const primaryBtn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    px: '2.25rem', py: '0.95rem', borderRadius: '10px', minWidth: 200,
    fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: '#fff', bgcolor: C.green,
    cursor: 'pointer', border: 'none', textTransform: 'none', lineHeight: 1.2,
    transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(8,80,65,0.30)', opacity: 0.95 },
    '&:focus-visible': { outline: `3px solid ${C.ctaAccent}`, outlineOffset: 2 },
  };

  return (
    <Box sx={{ fontFamily: FONT, bgcolor: '#fff', color: C.ink, lineHeight: 1.5 }}>
      <PublicHeader />

      {/* ── HERO (deep-teal builder zone) ── */}
      <Box component="section" sx={{
        position: 'relative', overflow: 'hidden', color: '#fff',
        px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3.5rem,7vw,6rem)',
        background: `radial-gradient(1100px 520px at 75% -10%, #1f6f6a 0%, ${C.green} 42%, ${C.greenDeep} 100%)`,
      }}>
        {/* subtle grid texture */}
        <Box aria-hidden sx={{
          position: 'absolute', inset: 0, opacity: 0.06, zIndex: 0,
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '46px 46px',
        }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1100, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)',
            fontSize: '0.78rem', fontWeight: 700, px: '0.95rem', py: '0.4rem', borderRadius: '100px', mb: '1.25rem',
          }}>
            <Engineering sx={{ fontSize: 16 }} /> For builders &amp; contractors
          </Box>
          <Box component="h1" sx={{
            fontFamily: FONT, fontWeight: 800, fontSize: 'clamp(2.3rem,5vw,4rem)', lineHeight: 1.12,
            letterSpacing: '-0.04em', m: 0, mb: '1rem',
          }}>
            Build for the diaspora.<br />
            <Box component="span" sx={{ color: C.ctaAccent }}>Get paid for every milestone.</Box>
          </Box>
          <Box component="p" sx={{
            fontSize: 'clamp(0.98rem,1.4vw,1.15rem)', color: 'rgba(255,255,255,0.9)',
            maxWidth: 640, mx: 'auto', mt: '0.5rem', mb: '2rem', fontWeight: 500, lineHeight: 1.65,
          }}>
            Join Eqaya&apos;s network of <strong style={{ color: '#fff', fontWeight: 800 }}>verified</strong> Zimbabwean
            contractors. Win real, funded projects from clients abroad — and get paid securely as each
            stage is signed off. Applying is free.
          </Box>
          <Box sx={{ display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box component="button" onClick={go(APPLY)} sx={{ ...primaryBtn, bgcolor: '#fff', color: C.greenDeep, '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.25)', bgcolor: '#fff' } }}>
              Apply to become a verified contractor <ArrowForward sx={{ fontSize: 18 }} />
            </Box>
            <Box component="button" onClick={() => document.getElementById('requirements')?.scrollIntoView({ behavior: 'smooth' })} sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', px: '1.75rem', py: '0.95rem', borderRadius: '10px',
              fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: '#fff', cursor: 'pointer',
              bgcolor: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.6)', textTransform: 'none',
              transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 2 },
            }}>
              See what you&apos;ll need
            </Box>
          </Box>
          <Box component="p" sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.78)', fontWeight: 600, mt: '1.25rem' }}>
            Free to apply&nbsp;·&nbsp;Verified badge on approval&nbsp;·&nbsp;Paid per verified milestone
          </Box>
        </Box>
      </Box>

      {/* ── WHY JOIN ── */}
      <Box component="section" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)', maxWidth: 1400, mx: 'auto' }}>
        <SectionLabel>Why build with Eqaya</SectionLabel>
        <SectionH2>Work worth showing up for</SectionH2>
        <Box component="p" sx={{ textAlign: 'center', color: C.gray, maxWidth: 620, mx: 'auto', mb: '2.5rem', mt: 0, fontSize: 'clamp(0.9rem,1.1vw,1rem)', lineHeight: 1.7 }}>
          Eqaya connects you with diaspora clients who are ready to build — and makes sure you actually get paid for the work you do.
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4,1fr)' }, gap: { xs: '1.25rem', xl: '1.5rem' } }}>
          {benefits.map((b) => (
            <Box key={b.title} sx={{ display: 'flex', flexDirection: 'column', bgcolor: b.bg, borderRadius: '16px', p: 'clamp(1.5rem,2.2vw,2rem)' }}>
              <Box sx={{ width: 50, height: 50, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: b.accent, color: '#fff', mb: '1.1rem', '& .MuiSvgIcon-root': { fontSize: 25 } }}>
                {b.icon}
              </Box>
              <Box component="h3" sx={{ fontFamily: FONT, fontSize: '1.05rem', fontWeight: 700, color: C.ink2, m: 0, mb: '0.4rem' }}>{b.title}</Box>
              <Box component="p" sx={{ fontSize: '0.88rem', color: C.gray, lineHeight: 1.65, m: 0 }}>{b.body}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── THE RULE (deep-teal band: the one thing every contractor must know) ── */}
      <Box component="section" id="the-rule" sx={{ bgcolor: C.greenDeep, color: '#fff', px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,4.5rem)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
          <SectionLabel light>The one rule</SectionLabel>
          <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 800, lineHeight: 1.15, m: 0, mb: '1rem', textWrap: 'balance' }}>
            Only jobs paid through the Eqaya wallet build your name.
          </Box>
          <Box component="p" sx={{ fontSize: 'clamp(0.95rem,1.2vw,1.1rem)', lineHeight: 1.7, m: '0 auto 1.75rem', maxWidth: 680, color: 'rgba(255,255,255,0.88)' }}>
            Your reviews, your rating, your job count and your verified badge are shown to every client who looks at you.
            They come from one place: work a client paid for through the Eqaya project wallet.
            <strong> A cash job builds nothing here.</strong>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: '0.85rem', textAlign: 'left' }}>
            {[
              { t: 'Reviews', d: 'A client can only review you after a stage has been paid to you through Eqaya.' },
              { t: 'Job count', d: '"Jobs paid through Eqaya" counts every wallet that has released money to you. Clients see it on your card.' },
              { t: 'Phone calls are fine', d: 'Talk however you like. When you agree a price, send the quotation on Eqaya so the job is protected and counts.' },
              { t: 'Quote in stages', d: 'Nobody replies to USD 24,000. They do reply to "start with USD 7,000". Split every quotation into stages: the client funds one at a time, you are paid as each is approved, and you never start an unfunded stage.' },
            ].map((r) => (
              <Box key={r.t} sx={{ border: '1.5px solid rgba(255,255,255,0.22)', borderRadius: '14px', p: '1.1rem 1.25rem', bgcolor: 'rgba(255,255,255,0.06)' }}>
                <Box sx={{ fontSize: '0.95rem', fontWeight: 700, mb: '0.25rem', color: C.ctaAccent }}>{r.t}</Box>
                <Box sx={{ fontSize: '0.85rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.88)' }}>{r.d}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── HOW APPLYING WORKS ── */}
      <Box component="section" sx={{ bgcolor: C.tintBg, px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <SectionLabel>How applying works</SectionLabel>
          <SectionH2>From application to first job</SectionH2>
          <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4,1fr)' },
            border: `1.5px solid ${C.line}`, borderRadius: '16px', overflow: 'hidden', mt: '2rem', bgcolor: '#fff',
          }}>
            {steps.map((s, i) => (
              <Box key={s.badge} sx={{
                p: { xs: 'clamp(1.25rem,2vw,2rem) clamp(1rem,1.5vw,1.5rem)', xl: '2.25rem 2rem' },
                borderRight: { md: i < 3 ? `1.5px solid ${C.line}` : 'none' },
                borderTop: { xs: i > 0 ? `1.5px solid ${C.line}` : 'none', sm: i > 1 ? `1.5px solid ${C.line}` : 'none', md: 'none' },
              }}>
                <Box sx={{ display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, px: '0.55rem', py: '0.2rem', borderRadius: '100px', mb: '1rem', letterSpacing: '0.04em', bgcolor: C.greenPillBg, color: C.greenDeep }}>
                  {s.badge}
                </Box>
                <Box sx={{ mb: '0.85rem', color: C.green, '& .MuiSvgIcon-root': { fontSize: 30 } }}>{s.icon}</Box>
                <Box component="h4" sx={{ fontFamily: FONT, fontSize: '1rem', fontWeight: 700, color: C.ink2, m: 0, mb: '0.35rem' }}>{s.title}</Box>
                <Box component="p" sx={{ fontSize: '0.82rem', color: C.grayFaint, lineHeight: 1.55, m: 0 }}>{s.body}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── REQUIREMENTS ── */}
      <Box component="section" id="requirements" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)', maxWidth: 1100, mx: 'auto', scrollMarginTop: '80px' }}>
        <SectionLabel>Before you start</SectionLabel>
        <SectionH2>What you&apos;ll need to apply</SectionH2>
        <Box component="p" sx={{ textAlign: 'center', color: C.gray, maxWidth: 620, mx: 'auto', mb: '2.5rem', mt: 0, fontSize: 'clamp(0.9rem,1.1vw,1rem)', lineHeight: 1.7 }}>
          Have these ready and your application takes about 15 minutes. You can save your progress and finish later.
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '0.85rem' }}>
          {requirements.map((r) => (
            <Box key={r.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', border: `1.5px solid ${C.line}`, borderRadius: '14px', p: '1.1rem 1.25rem', bgcolor: '#fff' }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', flexShrink: 0, bgcolor: C.greenPillBg, color: C.greenDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: 21 } }}>
                {r.icon}
              </Box>
              <Box>
                <Box sx={{ fontSize: '0.95rem', fontWeight: 700, color: C.ink2, mb: '0.15rem' }}>{r.label}</Box>
                <Box sx={{ fontSize: '0.82rem', color: C.gray, lineHeight: 1.55 }}>{r.note}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box component="section" sx={{ bgcolor: C.tintBg, px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)' }}>
        <Box sx={{ maxWidth: 820, mx: 'auto' }}>
          <SectionLabel>Questions</SectionLabel>
          <SectionH2>Good to know before you apply</SectionH2>
          <Box sx={{ mt: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <Box key={f.q} sx={{ border: `1.5px solid ${C.line}`, borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
                  <Box component="button" onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open} sx={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
                    px: '1.25rem', py: '1.1rem', bgcolor: 'transparent', border: 'none', cursor: 'pointer',
                    fontFamily: FONT, fontSize: '0.98rem', fontWeight: 700, color: C.ink2, textAlign: 'left',
                    '&:focus-visible': { outline: `3px solid ${C.green}`, outlineOffset: -3 },
                  }}>
                    {f.q}
                    <ExpandMore sx={{ fontSize: 22, color: C.green, flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
                  </Box>
                  {open && (
                    <Box component="p" sx={{ px: '1.25rem', pb: '1.25rem', m: 0, fontSize: '0.9rem', color: C.gray, lineHeight: 1.7 }}>
                      {f.a}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── FINAL CTA ── */}
      <Box component="section" sx={{ bgcolor: C.ctaBg, px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3.5rem,6vw,6rem)', textAlign: 'center' }}>
        <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', mb: '0.75rem', lineHeight: 1.15, m: 0 }}>
          Ready to build<br /><Box component="span" sx={{ color: C.ctaAccent }}>with the diaspora?</Box>
        </Box>
        <Box component="p" sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.55)', maxWidth: 440, mx: 'auto', mb: '2rem', mt: '0.75rem', lineHeight: 1.65 }}>
          Apply today, get verified, and start winning funded projects from Zimbabweans around the world.
        </Box>
        <Box component="button" onClick={go(APPLY)} sx={{ ...primaryBtn, bgcolor: C.green }}>
          Apply to become a verified contractor <ArrowForward sx={{ fontSize: 18 }} />
        </Box>
        <Box component="p" sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', mt: '1rem' }}>
          Already applied?{' '}
          <Box component="span" onClick={go('/login?role=contractor')} sx={{ color: C.ctaAccent, fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            Log in
          </Box>
        </Box>
      </Box>

      {/* ── FOOTER ── */}
      <Box component="footer" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(2rem,4vw,3rem)', borderTop: `1px solid ${C.line}`, bgcolor: '#fff', textAlign: 'center' }}>
        <Box sx={{ fontSize: '0.78rem', color: '#9a9a9a' }}>
          © 2026 Eqaya · Built for the Zimbabwean diaspora
        </Box>
      </Box>
    </Box>
  );
};

export default ForContractorsPage;
